import React, { useEffect, useState } from 'react';
import { Button, Form, notification, Select } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import ApplicantNamePicker from '../ApplicantNamePicker';
import CustomInput from '../CustomComponents/CustomInput';
import {
  ADD_EVENT,
  CHECK_ONE_STOP_APPLICATION_EXISTENCE,
  GET_IAR_APPLICATION_DETAIL,
  GET_PROCEEDING_EVENT_DETAILS,
  UPDATE_PROCEEDING_EVENT,
  sourceOptions,
  typeOptions,
} from './ProceedingTypeConstants';
import { useIsAuthenticated } from '@azure/msal-react';

interface EventFormProps {
  onSubmit: (success: boolean) => void;
  onCancel: () => void;
  eventId: string | null;
  proceedingId: string;
  form: any;
}

interface ApplicationDetails {
  exists: boolean;
  proceedingNumberIarApplication: any;
}

const EventForm: React.FC<EventFormProps> = ({
  onSubmit,
  onCancel,
  eventId,
  proceedingId,
  form,
}) => {
  const [selectedBaId, setSelectedBaId] = useState<string | null>(null);
  const [initialTypeNumber, setInitialTypeNumber] = useState<string>('');
  const [applicationDetails, setApplicationDetails] = useState<ApplicationDetails>(null);
  const [overwrite, setOverwrite] = useState<boolean>(false);
  const [oneStopApplicationExists, setOneStopApplicationExists] = useState<boolean>(true);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<any>(null);
  const isAuthenticated = useIsAuthenticated();

  const [createNewEvent, { loading: creatingEvent, error: createError, data: newEvent }] =
    useMutation(ADD_EVENT);
  const [getIarApplicationDetails] = useLazyQuery(GET_IAR_APPLICATION_DETAIL);
  const [checkOneStopApplicationExistence] = useLazyQuery(CHECK_ONE_STOP_APPLICATION_EXISTENCE);
  const [
    getEventDetails,
    { loading: loadingEventDetails, error: eventDetailsError, data: eventDetailsData },
  ] = useLazyQuery(GET_PROCEEDING_EVENT_DETAILS);
  const [updateEvent, { loading: updatingEvent, error: updateError, data: updatedEvent }] =
    useMutation(UPDATE_PROCEEDING_EVENT);

  const onFinish = async (values: any) => {
    setFormValues(values);
    setOneStopApplicationExists(true);
    if (values.type === 'Application' && (!values.typeNumber || !values.source)) {
      notification.error({
        message: 'Proceeding type source and number are mandatory for Application',
      });
      return false;
    }

    if (values.type === 'Application') {
      if (!overwrite && values.source === 'IAR') {
        const details = await getIarApplicationDetails({
          variables: { input: values.typeNumber },
        });
        setApplicationDetails(details?.data?.iarApplication?.data);
        if (!details?.data?.iarApplication?.data?.exists || !overwrite) return;
      } else if (values.source === 'OneStop 1.0') {
        const result = await checkOneStopApplicationExistence({
          variables: { input: values.typeNumber },
        });
        setOneStopApplicationExists(result?.data?.oneStopApplication?.data?.exists);
        if (!result?.data?.oneStopApplication?.data?.exists) return;
      }
    }

    const mutation = eventId ? updateEvent : createNewEvent;

    await mutation({
      variables: {
        input: {
          id: eventId || undefined,
          proceedingId: proceedingId,
          applicantName: selectedBaId ? null : values.applicantName,
          applicantBaId: selectedBaId || null,
          description: values.description,
          typeNumber: values.typeNumber,
          source: values.source,
          type: values.type,
        },
      },
    });
  };

  useEffect(() => {
    if (eventId) {
      getEventDetails({ variables: { input: { proceedingId, eventId } } });
    }
  }, [eventId, proceedingId, getEventDetails]);

  useEffect(() => {
    if (eventDetailsData?.eventDetails) {
      form.setFieldsValue(eventDetailsData.eventDetails);
      setSelectedBaId(eventDetailsData.eventDetails.applicantBaId);
    }
  }, [eventDetailsData, form]);

  useEffect(() => {
    if (applicationDetails !== null) {
      setOverwrite(applicationDetails.exists);
    }
  }, [showWarning, applicationDetails]);

  useEffect(() => {
    if (!oneStopApplicationExists) {
      notification.error({ message: 'Application # does not exists in One Stop 1.0 system' });
    }
  }, [oneStopApplicationExists]);

  useEffect(() => {
    if (!applicationDetails) return;

    const { exists, proceedingNumberIarApplication } = applicationDetails;

    !exists &&
      !proceedingNumberIarApplication &&
      notification.error({ message: 'Application # does not exist in the IAR system' });

    if (exists) {
      proceedingNumberIarApplication && proceedingId !== proceedingNumberIarApplication
        ? (setShowWarning(true),
          notification.warning({
            message: `Application # is associated with another proceeding: ${proceedingNumberIarApplication}`,
          }))
        : (setOverwrite(true), onFinish(formValues));
    }
  }, [applicationDetails]);

  useEffect(() => {
    const result =
      newEvent?.addProceedingEvent?.result || updatedEvent?.updateProceedingEvent?.result;

    if (result === 'Success') {
      form.resetFields();
      onSubmit(true);
    } else if (createError || updateError) {
      notification.error({
        message: 'Error',
        description: createError?.message || updateError?.message,
      });
      onSubmit(false);
    } else {
      const errorMessage = result?.includes('Error in deleting proceeding event')
        ? 'Error in deleting proceeding event after IAR marker failed. Please delete event manually.'
        : result?.includes('Error in updating IAR marker')
        ? 'Error in updating IAR marker. Please contact support.'
        : null;

      if (errorMessage || createError || updateError) {
        if (errorMessage) notification.error({ message: errorMessage });
        onSubmit(false);
      }
    }
  }, [newEvent, updatedEvent, createError, updateError, form, onSubmit]);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (initialTypeNumber !== event.target.value) {
      setInitialTypeNumber(event.target.value);
      setShowWarning(false);
      setOverwrite(false);
      setApplicationDetails(null);
    }
  };
  return (
    <div className="thin-border">
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          className="type-of-proceeding-event"
          name="type"
          label="Proceeding type"
          rules={[{ required: true, message: 'Please select the type' }]}
        >
          <Select
            options={typeOptions}
            disabled={!isAuthenticated}
            data-testid="createEventSelectType"
          />
        </Form.Item>
        <Form.Item
          className="name-of-applicant"
          name="applicantName"
          label="Name of Applicant or Requestor or Approval Holder"
          rules={[{ required: true, whitespace: true, message: 'Please provide applicant name' }]}
        >
          <ApplicantNamePicker
            onChange={(value) => form.setFieldsValue({ applicantName: value })}
            disabled={!isAuthenticated}
            onSelect={(baId, isLookup, value) => {
              setSelectedBaId(baId);
              form.setFieldsValue({ applicantName: value });
            }}
            data-testid="createEventApplicantName"
          />
        </Form.Item>
        <Form.Item className="type-number" name="typeNumber" label="Proceeding type number">
          <CustomInput
            showInput={true}
            disabled={!isAuthenticated}
            onChange={(e) => handleInputChange(e)}
          ></CustomInput>
        </Form.Item>
        <Form.Item
          className="description-of-event"
          name="description"
          label="Description of the type"
          data-testid="createEventDescription"
        >
          <CustomInput showInput={true} disabled={!isAuthenticated}></CustomInput>
        </Form.Item>
        <Form.Item className="proceeding-event-source" name="source" label="Proceeding type source">
          <Select
            options={sourceOptions}
            id="testSource"
            disabled={!isAuthenticated}
            data-testid="createEventSelectSource"
          />
        </Form.Item>
        {showWarning && isAuthenticated && (
          <p style={{ color: 'red' }}>
            Application is associated with another proceeding #{applicationDetails.proceedingNumberIarApplication}
            . Do you want to proceed?
          </p>
        )}
        <Form.Item>
          {isAuthenticated && (
            <Button type="primary" htmlType="submit" data-testid="createEventBtn">
              {showWarning ? 'Yes, Overwrite' : 'Submit'}
            </Button>
          )}
          <Button onClick={onCancel} data-testid="createEventCancelBtn" style={{ marginLeft: 8 }}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default EventForm;
